import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { getwoundprogressAsync, ne1updateassessmentprogressAsync } from "../../../../../app/patientSlice";

export const useCharacteristicProgress = ({
  index,
  updateDone,
  selectedIndex,
  patientId,
  assessmentId,
  assessmentDetails,
  getCharDetails,
  overrideToggle,
  isCompleted,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { register, handleSubmit, reset } = useForm();

  const woundprogressData = useSelector((state) => state.patient.woundprogressDataByAssessID);
  const ne1getWounAssessmentDetailData = useSelector((state) => state.patient.ne1getWounAssessmentDetail);
  const getUserRole = useSelector((state) => state.user);
  const woundProgressOptions = useSelector((state) => state.patient.woundProgress);
  const charStatus = useSelector((state) => state.patient.charStatus);

  const [editable, setEditable] = useState(false);
  const [formChangesConfirm, setFormChangesConfirm] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [clickCancel, setClickCancel] = useState(false);
  const [assessmentDetailsNew, setAssessmentDetailsNew] = useState("");
  const [isProgressResolve, setIsProgressResolve] = useState(false);

  const buildNavigateFn = useCallback(() => {
    updateDone(index);
    if (Array.isArray(charStatus) && charStatus[index] === 1) {
      setEditable(false);
    } else {
      selectedIndex(0);
    }
  }, [charStatus, index, selectedIndex, updateDone]);

  const confirmationClose = useCallback(() => {
    setConfirmationModal(false);
    setClickCancel(false);
  }, []);

  const yesConfirmationModalClick = useCallback(() => {
    setConfirmationModal(false);
    setEditable(false);
    setFormChangesConfirm(false);
    setClickCancel(false);
  }, []);

  const woundProgressResolve = useCallback(() => {
    setIsProgressResolve(false);
  }, []);

  const handleYesProgress = useCallback(
    (isNo) => {
      let tempAssessment = { woundStage: "resolve" };

      if (isNo === 1 || isNo === 0) {
        let tempMeasurement = { ...assessmentDetails?.measurement };
        if (isNo === 1) {
          tempMeasurement.surfaceArea = 0;
          tempMeasurement.length = 0;
          tempMeasurement.width = 0;
          tempMeasurement.depth = 0;
          tempMeasurement.undermining = 0;
          tempMeasurement.tunneling = 0;
        }
        tempMeasurement.isMeasurementResolved = isNo === 1 ? 1 : 0;
        tempAssessment = { ...tempAssessment, measurement: tempMeasurement };
      }

      const payload = {
        patientId,
        assessmentId,
        assessment: { ...assessmentDetails, ...tempAssessment },
        navigate: buildNavigateFn,
        getCharDetails,
        overrideenable: overrideToggle ? true : false,
        IsMeasurementResolvedManually: isNo === 1 ? 1 : 0,
      };

      dispatch(ne1updateassessmentprogressAsync(payload, navigate));
      setIsProgressResolve(false);
    },
    [assessmentDetails, buildNavigateFn, dispatch, getCharDetails, navigate, overrideToggle, patientId, assessmentId]
  );

  const handleNoProgress = useCallback(() => {
    handleYesProgress(0);
  }, [handleYesProgress]);

  const doSubmit = useCallback(
    (data) => {
      if (data.progress === "resolve") {
        setIsProgressResolve(true);
      } else {
        setIsProgressResolve(false);
        const payload = {
          patientId,
          assessmentId,
          assessment: { ...assessmentDetails, woundStage: data.progress },
          navigate: buildNavigateFn,
          getCharDetails,
          overrideenable: overrideToggle ? true : false,
        };
        dispatch(ne1updateassessmentprogressAsync(payload, navigate));
      }
    },
    [assessmentDetails, buildNavigateFn, dispatch, getCharDetails, navigate, overrideToggle, patientId, assessmentId]
  );

  useEffect(() => {
    if (!editable) return;
    if (formChangesConfirm && clickCancel) {
      setConfirmationModal(true);
    } else if (clickCancel && !formChangesConfirm) {
      setClickCancel(false);
      setConfirmationModal(false);
      setEditable(false);
    }
  }, [formChangesConfirm, clickCancel, editable]);

  useEffect(() => {
    setAssessmentDetailsNew("");
    toast.dismiss("success1");
    reset({});
    dispatch(getwoundprogressAsync({ patientId, assessmentId }, navigate));
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (woundprogressData) {
      setAssessmentDetailsNew({ woundStage: woundprogressData?.woundProgress });
    }
  }, [woundprogressData]);

  useEffect(() => {
    if (isCompleted === 0) {
      setAssessmentDetailsNew("");
      setEditable(true);
      window.scrollTo({ top: 500, behavior: "smooth" });
    }
  }, [assessmentDetailsNew, isCompleted]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    register,
    handleSubmit,
    editable,
    setEditable,
    setFormChangesConfirm,
    confirmationModal,
    isProgressResolve,
    assessmentDetailsNew,
    getUserRole,
    woundProgressOptions,
    ne1getWounAssessmentDetailData,
    confirmationClose,
    yesConfirmationModalClick,
    woundProgressResolve,
    handleYesProgress,
    handleNoProgress,
    doSubmit,
    setClickCancel,
  };
};
